import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "../components/Logo";
import { Truck, ArrowRight, MapPin, Clock, Phone, EnvelopeSimple, CheckCircle, Sparkle } from "@phosphor-icons/react";
import client from "../lib/api";
import { trackConversion, getAttribution } from "../lib/analytics";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  "AI Dispatch Automation",
  "AI Customer Service Assistant",
  "Invoice & Billing Automation",
  "Fleet Maintenance Automation",
  "Driver Recruiting Automation",
  "Not sure — need consultation",
];

const QUESTIONS = [
  "How can we reduce dispatch time?",
  "How can we automate customer communication?",
  "How can we improve invoicing and collections?",
  "How can we reduce administrative costs?",
];

// Pre-fill map for intent-driven landings. Extendable — add new intents
// here without touching the form component. Aliases let the URL use the
// short `pilot` form while the backend records the canonical value.
const INTENT_ALIASES = {
  pilot: "founding_pilot",
  "free-workflow": "free_workflow",
  "concierge-setup": "concierge_setup",
};
const INTENT_PRESETS = {
  founding_pilot: {
    services_of_interest: "15-min setup call",
    current_challenges:
      "I'd like a 15-min setup call to get started on the 7-day trial.",
    banner: {
      overline: "Book a setup call",
      headline: "Tell us about your operation.",
      subtext:
        "Fill this out and I'll personally reply within a few hours — we're available 24/7 — to book a 15-min call. Start your 7-day free trial first (card on file, $0 today) and I'll set it up live with you.",
    },
  },
  free_workflow: {
    services_of_interest: "Free Workflow Automation",
    current_challenges:
      "I'd like FreightPilot to automate one workflow for me for free — please walk me through what you can build for my operation.",
    banner: {
      overline: "First workflow — set up with you",
      headline: "Tell us what to automate first.",
      subtext:
        "Share what eats the most time in your operation right now (dispatch, invoicing, driver comms, IFTA…). We'll set that workflow up with you during your 7-day free trial, live in ≤ 5 business days.",
    },
  },
  concierge_setup: {
    services_of_interest: "Concierge Setup",
    current_challenges:
      "I'd like the founder to set up my dispatch, invoicing & driver comms for me on a 30-min screen share during my 7-day trial.",
    banner: {
      overline: "Concierge setup — during your 7-day trial",
      headline: "I'll set it up for you personally.",
      subtext:
        "Book a 30-min screen share and I (Michael, the founder) will configure your dispatch, invoicing and driver comms live while you watch. You start a 7-day free trial (card on file, $0 today). If you like it, it keeps running. If not, cancel before day 8 — no charge, no hard feelings.",
    },
  },
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const rawIntent = searchParams.get("intent") || "";
  // Resolve URL alias → canonical intent key.
  const resolvedIntent = INTENT_ALIASES[rawIntent] || rawIntent;
  const intent = INTENT_PRESETS[resolvedIntent] ? resolvedIntent : "general";
  const preset = INTENT_PRESETS[resolvedIntent];

  // Attribution: pass whatever `utm_source` the landing page carried (tiktok,
  // instagram_reels, meta, etc.) straight into the contact row so channel
  // dashboards + short-form auto-reply SMS can key off it server-side.
  const utm_source = (searchParams.get("utm_source") || "").trim().toLowerCase();
  const utm_medium = (searchParams.get("utm_medium") || "").trim().toLowerCase();
  const utm_campaign = (searchParams.get("utm_campaign") || "").trim().toLowerCase();

  const [form, setForm] = useState({
    company_name: "", contact_name: "", phone: "", email: "",
    fleet_size: "",
    current_challenges: preset?.current_challenges || "",
    services_of_interest: preset?.services_of_interest || "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // If the URL changes (e.g. deep-link during SPA nav) refresh the presets.
  useEffect(() => {
    if (preset) {
      setForm((f) => ({
        ...f,
        services_of_interest: f.services_of_interest || preset.services_of_interest,
        current_challenges: f.current_challenges || preset.current_challenges,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawIntent]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/contact", { ...form, intent, utm_source, utm_medium, utm_campaign, ...getAttribution() });
      trackConversion("contact_submit", {
        content_name:
          intent === "founding_pilot"
            ? "Setup Call Request"
            : intent === "free_workflow"
            ? "Free Workflow Automation Request"
            : intent === "concierge_setup"
            ? "Concierge Setup Request"
            : "Free Workflow Consultation",
        content_category: form.services_of_interest || "General",
        fleet_size: form.fleet_size,
        intent,
      });
      setDone(true);
      toast.success(
        intent === "founding_pilot"
          ? "Got it. I'll personally reply within a few hours — we're on 24/7 — to book your call."
          : intent === "free_workflow"
          ? "Got it — we'll reach out within a few hours (24/7) to set up your first workflow with you."
          : intent === "concierge_setup"
          ? "Got it. I'll personally text or call you within 4 hours to lock in your setup slot."
          : "Request received. We're available 24/7 — expect a reply within a few hours."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative noise-bg overflow-hidden" data-testid="contact-page">
      {/* Header */}
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="contact-logo-link">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-[#FF5A1F] flex items-center justify-center shrink-0">
              <Truck size={18} weight="bold" color="#0A0A0A" />
            </div>
            <span className="font-display text-base sm:text-lg font-semibold tracking-tight truncate">FREIGHTPILOT AI</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/services" className="text-neutral-400 hover:text-white transition-colors" data-testid="contact-services-link">Services</Link>
            <Link to="/login" className="text-neutral-400 hover:text-white transition-colors" data-testid="contact-signin-link">Sign in</Link>
            <Link to="/signup" data-testid="contact-getstarted-btn" className="btn-primary rounded-full px-4 py-2 inline-flex items-center gap-2">
              Get started <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <Link to="/signup" className="md:hidden btn-primary rounded-full px-3 py-1.5 text-xs whitespace-nowrap">Try free</Link>
        </div>
      </header>

      {/* Hero — copy morphs by URL ?intent */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 pt-24 pb-12">
        <div className="max-w-3xl">
          {preset ? (
            <>
              <div className="overline mb-4 text-[#FF9764] flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> {preset.banner.overline}
              </div>
              <h1
                data-testid="contact-hero-pilot"
                className="font-display text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]"
              >
                {preset.banner.headline}
              </h1>
              <p className="text-neutral-300 text-lg mt-6 leading-relaxed">
                {preset.banner.subtext}
              </p>
            </>
          ) : (
            <>
              <div className="overline mb-4">CONTACT FREIGHTPILOT AI LLC</div>
              <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
                Ready to improve your trucking operation with{" "}
                <span className="text-[#FF5A1F]">AI automation?</span>
              </h1>
              <p className="text-neutral-400 text-lg mt-6 leading-relaxed">
                We'd be happy to discuss your business goals and identify opportunities to reduce manual work
                and increase efficiency.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Content grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info column */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="card-surface p-8">
              <div className="overline mb-4">CONTACT INFORMATION</div>

              <InfoRow icon={<Truck size={16} weight="regular" />} label="Business name" value="Freight Pilot AI LLC (dba FreightPilot AI™)" testid="info-business" />
              <InfoRow icon={<MapPin size={16} />} label="Business address" value="Sioux Falls, SD" testid="info-location" />
              <InfoRow icon={<Phone size={16} />} label="Phone" value="(605) 679-7303" href="tel:+16056797303" testid="info-phone" />
              <InfoRow icon={<EnvelopeSimple size={16} />} label="Email" value="contact@freightpilotai.ai" href="mailto:contact@freightpilotai.ai" testid="info-email" />
              <InfoRow icon={<EnvelopeSimple size={16} />} label="Owner direct" value="michaeleguley@gmail.com" href="mailto:michaeleguley@gmail.com" testid="info-owner-email" last />
            </div>

            <div className="card-surface p-8">
              <div className="flex items-center gap-2 mb-5">
                <Clock size={18} weight="regular" color="#FF5A1F" />
                <div className="overline">AVAILABILITY</div>
              </div>
              <HoursRow day="Every day — including weekends" time="24/7" last />
              <p className="text-sm text-neutral-400 leading-relaxed mt-4">
                Our automations never sleep, and neither does our intake — requests are
                monitored around the clock, with replies within a few hours.
              </p>
            </div>

            <div className="card-surface p-8">
              <div className="overline mb-4">EXAMPLE QUESTIONS WE CAN ANSWER</div>
              <ul className="space-y-3">
                {QUESTIONS.map((q, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed">
                    <CheckCircle size={14} weight="fill" color="#FF5A1F" className="mt-1 shrink-0" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form column */}
          <div className="lg:col-span-3">
            <div className="card-surface p-8 md:p-10">
              {done ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} weight="fill" color="#10B981" />
                  </div>
                  <h3 className="font-display text-3xl font-semibold tracking-tight">Request received</h3>
                  <p className="text-neutral-400 mt-4 leading-relaxed max-w-md mx-auto">
                    Thanks, {form.contact_name.split(" ")[0] || "friend"}. Our team is available 24/7 — we'll reach out
                    within a few hours to schedule your free workflow review.
                  </p>
                  <Link to="/" data-testid="contact-back-home" className="inline-flex items-center gap-2 mt-8 text-[#FF5A1F] hover:text-[#FF7038] font-medium text-sm">
                    Back to home <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              ) : (
                <>
                  <div className="overline mb-3">REQUEST A FREE CONSULTATION</div>
                  <h2 className="font-display text-3xl font-semibold tracking-tight mb-2">Tell us about your fleet.</h2>
                  <p className="text-neutral-400 text-sm mb-8">We're available 24/7 — replies within a few hours. No obligations, no spam.</p>

                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field label="Company name" required>
                        <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                          data-testid="contact-company-input"
                          className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="Your trucking company" />
                      </Field>
                      <Field label="Contact name" required>
                        <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                          data-testid="contact-name-input"
                          className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="Your full name" />
                      </Field>
                      <Field label="Phone number" required>
                        <input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                          data-testid="contact-phone-input"
                          className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="(555) 555-5555" />
                      </Field>
                      <Field label="Email address" required>
                        <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                          data-testid="contact-email-input"
                          className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600" placeholder="you@fleet.com" />
                      </Field>
                      <Field label="Number of trucks in fleet">
                        <select value={form.fleet_size} onChange={(e) => set("fleet_size", e.target.value)}
                          data-testid="contact-fleet-select"
                          className="w-full bg-transparent outline-none py-3 text-sm text-neutral-200">
                          <option value="" className="bg-[#141414]">Select fleet size…</option>
                          <option value="1-5" className="bg-[#141414]">1 – 5 trucks</option>
                          <option value="6-10" className="bg-[#141414]">6 – 10 trucks</option>
                          <option value="11-25" className="bg-[#141414]">11 – 25 trucks</option>
                          <option value="26-50" className="bg-[#141414]">26 – 50 trucks</option>
                          <option value="50+" className="bg-[#141414]">50+ trucks</option>
                        </select>
                      </Field>
                      <Field label="Services of interest">
                        <select value={form.services_of_interest} onChange={(e) => set("services_of_interest", e.target.value)}
                          data-testid="contact-services-select"
                          className="w-full bg-transparent outline-none py-3 text-sm text-neutral-200">
                          <option value="" className="bg-[#141414]">Select a service…</option>
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s} className="bg-[#141414]">{s}</option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <Field label="Current challenges" span={2}>
                      <textarea value={form.current_challenges} onChange={(e) => set("current_challenges", e.target.value)}
                        data-testid="contact-challenges-input"
                        rows={4}
                        className="w-full bg-transparent outline-none py-3 text-sm placeholder:text-neutral-600 resize-none"
                        placeholder="Tell us what's slowing your operation down — dispatch bottlenecks, paperwork, driver comms, invoicing, anything." />
                    </Field>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="w-full btn-primary rounded-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-4"
                    >
                      {loading ? "Sending..." : "Request my free consultation"}
                      <ArrowRight size={16} weight="bold" />
                    </button>

                    <p className="text-xs text-neutral-500 text-center pt-2">
                      By submitting, you agree to be contacted by Freight Pilot AI LLC about your consultation.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 py-24 border-t border-white/10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline mb-4">GET STARTED TODAY</div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
            Discover how AI can help your trucking business operate more efficiently and profitably.
          </h2>
          <Link to="/signup" data-testid="contact-cta-signup" className="btn-primary rounded-full px-8 py-4 inline-flex items-center gap-2 mt-10">
            Create your fleet account <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        FREIGHT PILOT AI LLC • Sioux Falls, South Dakota • FreightPilot AI™
      </footer>
    </div>
  );
}

function InfoRow({ icon, label, value, href, testid, last }) {
  const content = <span className="text-white font-medium">{value}</span>;
  return (
    <div className={`flex items-start gap-4 py-3 ${last ? "" : "border-b border-white/5"}`} data-testid={testid}>
      <span className="text-[#FF5A1F] mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">{label}</div>
        {href ? <a href={href} className="hover:text-[#FF5A1F] transition-colors">{content}</a> : content}
      </div>
    </div>
  );
}

function HoursRow({ day, time, last }) {
  return (
    <div className={`flex items-center justify-between py-2.5 text-sm ${last ? "" : "border-b border-white/5"}`}>
      <span className="text-neutral-300">{day}</span>
      <span className="font-mono text-neutral-100">{time}</span>
    </div>
  );
}

function Field({ label, required, children, span }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <div className="overline mb-2">{label}{required && <span className="text-[#FF5A1F]"> *</span>}</div>
      <div className="px-4 border border-white/10 rounded-md bg-[#141414] focus-within:border-[#FF5A1F]/60 transition-colors">
        {children}
      </div>
    </label>
  );
}
